import { Typography, Button, Input } from '@mgmresorts/wcl-lab';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react';

import { KNOWN_ERROR_MESSAGES } from '../../api/constants';
import { checkUser } from '../../api/fetch/signInOrJoin';
import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { EventAttributes } from '../../utils/analytics/types';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { MaxLengths } from '../../utils/constants';
import { PersistentParametersContext } from '../../utils/persistParameterContext';
import { isBetMGMClient } from '../../utils/redirect';
import { UserAccountContext } from '../../utils/userAccountContext';
import { emailValidator } from '../../utils/validators';

import { StyledContent } from './styled';

export function SignInOrJoin() {
  const trackEvent = useEvent();
  usePageLoadEvent('identity', 'home');
  const [error, setError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { validateField, invalidFields } = useFieldValidation([emailValidator]);
  const { getContentByPath } = useContent();
  const { setUserEmail } = useContext(UserAccountContext);
  const queryParams = qs.parse(window.location.search);
  const { params } = useContext(PersistentParametersContext);
  const { email, client_id, redirect_uri } = queryParams;
  let emailId = '';
  if (email) {
    emailId = email as string;
  }
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  const { bind: bindUsername, value: login } = useInput(emailId, 'email', validateField, validateField);
  const pageTitle: string = isBetMGMClient(client_id as string)
    ? getContentByPath('signInOrJoin.betMgmTitle')
    : getContentByPath('signInOrJoin.title');

  useEffect(() => {
    if (invalidFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (invalidFields.includes('email') && !analyticsFieldErrors.includes('email')) {
        eventAttributes.errorField = 'Email';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.email.error');
      }

      if (eventAttributes.errorField) {
        trackEvent(
          'identity – identity home - email error',
          'email error identity home page',
          'emailerroridentityhome',
          eventAttributes,
        );
      }
    }

    setAnalyticsFieldErrors(invalidFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length]);

  const processSignInOrJoin = useCallback(
    async username => {
      try {
        const accountTypePath = {
          'sign-in': 'signin',
          'sign-up': 'signup',
          activate: 'activate',
          reset_password: 'forgot-password',
        };
        const trackAccountType = {
          'sign-up': 'JOIN',
        };
        setIsLoading(true);
        const { email, accountType } = await checkUser(username);
        if (!accountType) throw Error(KNOWN_ERROR_MESSAGES['invalid_token']);
        const userAccountType = accountType?.toLowerCase();
        setUserEmail(email);

        await trackEvent(
          'identity – next - path assignment click',
          'next',
          'nextpathclick',
          { pathAssigned: trackAccountType[userAccountType as keyof typeof trackAccountType] || accountType },
          true,
        );

        if (client_id === 'bet_mgm' && userAccountType !== 'sign-in') {
          throw Error(KNOWN_ERROR_MESSAGES['bet_mgm_error'].replaceAll('{REDIRECT_URI}', redirect_uri as string));
        } else {
          navigate(`/${accountTypePath[userAccountType as keyof typeof accountTypePath]}`, true);
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setUserEmail, trackEvent, redirect_uri, client_id],
  );

  const canRequestSignInOrJoin = useMemo(
    () => !invalidFields.length && login && !isLoading,
    [invalidFields.length, isLoading, login],
  );

  return (
    <StyledContent>
      <PageLayout testId='route-/' title={pageTitle} description={getContentByPath('signInOrJoin.description')}>
        <Container height='auto'>
          <>
            <Logo className='identity__page-logo' />
            <Typography variant='body' weight='regular' className='identity__page-heading' testId='page-title'>
              {pageTitle}
            </Typography>
            {error && <Alert text={error} data-testid='sign-in-or-join-error' />}
            {queryParams.message && getContentByPath(`signIn.message.${queryParams.message}`) && (
              <Alert type='info' marginTop='20px' text={getContentByPath(`signIn.message.${queryParams.message}`)} />
            )}
            {!queryParams.message && params.partner && getContentByPath(`signIn.message.partner.${params.partner}`) && (
              <Alert type='info' marginTop='20px' text={getContentByPath(`signIn.message.partner.${params.partner}`)} />
            )}
            <form className='identity__form'>
              <Input
                label={getContentByPath('signIn.fields.email.title')}
                id='email'
                testId='email'
                disabled={isLoading || !!emailId}
                aria-label={getContentByPath('signIn.fields.email.label')}
                className={`identity__field ${invalidFields.includes('email') ? 'with-error' : ''}`}
                error={invalidFields.includes('email')}
                hint={invalidFields.includes('email') ? getContentByPath('signIn.fields.email.error') : ''}
                {...bindUsername}
                maxLength={MaxLengths.Email}
              />
              <Button
                ariaLabel={getContentByPath('signInOrJoin.next')}
                onClick={e => {
                  e?.preventDefault();
                  processSignInOrJoin(login);
                }}
                onKeyPress={e => {
                  e.preventDefault();
                  if (e.key === 'Enter') {
                    processSignInOrJoin(login);
                  }
                }}
                disabled={!canRequestSignInOrJoin}
                sticky
                className='form-buttom identity__form-submit'
                testId='sign-in-or-join'
                type='submit'
                loading={isLoading}
                shape='square'
                size='large'
                variant='primary'
              >
                <Typography>{getContentByPath('signInOrJoin.next')}</Typography>
              </Button>
            </form>
          </>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

SignInOrJoin.displayName = 'SignInOrJoin';
